import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ButtonGroup,
  Chip,
  Stack,
  colors,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
  Divider,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import {
  useCreate,
  useList,
  useNavigation,
  useNotification,
  useShow,
  useUpdate,
} from "@refinedev/core";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import { v4 as uuid } from "uuid";
import { DragDropContext, DropResult, Droppable } from "react-beautiful-dnd";

import { FieldComponent } from "components/form/Field";
import { SectionItem } from "components/form/SectionItem";

import { Droppables } from "constants/enum";
import { theme } from "constants/theme";

import {
  CreateFormDto,
  FieldDto,
  FieldDtoFileTypeEnum,
  FieldEntityTypeEnum,
  FormEntity,
  SectionDto,
  SectionDtoTypeEnum,
} from "generated";

export type FieldTypeOptionsType = {
  label: string;
  value: string;
};

export type NestedQuestionsType = {
  fieldName: string;
  uniqueName: string;
  fieldType: string;
  fileType: string;
  fieldPlaceholder: string;
  acceptableValuesKey: string | null;
  checkboxOptions: { option: string }[];
  multiOptionsSelect: boolean;
  validate_required: boolean;
};

export type FormValuesType = {
  id: string;
  uniqueName: string;
  fieldName: string;
  fieldType: string;
  fileType: string;
  fieldPlaceholder: string;
  checkboxOption: string;
  acceptableValuesKey: string | null;
  checkboxOptions: { option: string }[];
  multiOptionsSelect: boolean;
  globalRef: string;
  validate_required: boolean;

  nestedQuestion: string;
  nestedQuestionUniqueName: string;
  nestedQuestionType: string;
  nestedFileType: string;
  nestedFieldPlaceholder: string;
  nestedCheckboxOption: string;
  nestedAcceptableValuesKey: string | null;
  nestedCheckboxOptions: { option: string }[];
  nestedMultiOptionsSelect: boolean;
  nested_validate_required: boolean;

  nestedQuestions: NestedQuestionsType[];
};

export type SectionFormValuesType = {
  sectionName: string;
  uniqueName: string;
  ref: string;
  required: boolean;
  repeat: boolean;
};

export type FormInfoValuesType = {
  formName: string;
  uniqueName: string;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
};

export type SectionType = {
  sectionId: string;
  uniqueName: string;
  sectionName: string;
  required: boolean;
  repeat: boolean;
  ref: string;
  fieldsList: FormValuesType[];
};

export type FieldType = "string" | "number" | "checkbox" | "files" | "array";

const fieldTypeOptions: FieldTypeOptionsType[] = [
  { label: "Text", value: "string" },
  { label: "Number", value: "number" },
  { label: "Checkbox", value: "checkbox" },
  { label: "Files", value: "files" },
  { label: "Array", value: "array" },
];

const questionTypeOptions: FieldTypeOptionsType[] = [
  { label: "Text", value: "string" },
  { label: "Number", value: "number" },
  { label: "Checkbox", value: "checkbox" },
  { label: "Files", value: "files" },
];

const fileTypeOptions: FieldTypeOptionsType[] = [
  { label: "Image", value: FieldDtoFileTypeEnum.Image },
  { label: "Video", value: FieldDtoFileTypeEnum.Video },
  { label: "Pdf", value: FieldDtoFileTypeEnum.Pdf },
  { label: "File", value: FieldDtoFileTypeEnum.File },
];

const formStatusTypeOptions: FieldTypeOptionsType[] = [
  { label: "Draft", value: "DRAFT" },
  { label: "Published", value: "PUBLISHED" },
  { label: "Archived", value: "ARCHIVED" },
];

const defaultFormValues: FormValuesType = {
  id: "",
  uniqueName: "",
  fieldName: "",
  fieldType: "",
  fileType: "",
  fieldPlaceholder: "",
  checkboxOption: "",
  acceptableValuesKey: null,
  checkboxOptions: [],

  nestedQuestion: "",
  nestedQuestionUniqueName: "",
  nestedQuestionType: "",
  nestedQuestions: [],

  nestedFieldPlaceholder: "",
  nestedCheckboxOption: "",
  nestedAcceptableValuesKey: null,
  nestedCheckboxOptions: [],
  nestedFileType: "",

  multiOptionsSelect: false,
  nestedMultiOptionsSelect: false,

  globalRef: "",

  validate_required: false,
  nested_validate_required: false,
};

const defaultSectionFormValues: SectionFormValuesType = {
  sectionName: "",
  uniqueName: "",

  ref: "",

  required: true,
  repeat: false,
};

const defaultFormInfoFormValues: FormInfoValuesType = {
  formName: "",
  uniqueName: "",
  status: "DRAFT",
};

const defaultEditInfo: {
  id: string;
  type: "field" | "section" | null;
} = {
  id: "",
  type: null,
};

export const FormCreate = () => {
  const { queryResult } = useShow();

  const { isSuccess } = queryResult;
  const formData = queryResult.data?.data as FormEntity | undefined;

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: defaultFormValues,
  });

  const {
    control: sectionFormControl,
    handleSubmit: handleSubmitSectionForm,
    reset: resetSectionForm,
    watch: watchSectionForm,
  } = useForm({
    defaultValues: defaultSectionFormValues,
  });

  const {
    control: formInfoFormControl,
    getValues,
    reset: resetFormInfoForm,
  } = useForm({
    defaultValues: defaultFormInfoFormValues,
  });

  const { fields, remove, insert } = useFieldArray({
    control,
    name: "checkboxOptions",
  });

  const {
    fields: nestedFields,
    remove: nestedRemove,
    insert: nestedInsert,
  } = useFieldArray({
    control,
    name: "nestedCheckboxOptions",
  });

  const {
    fields: questionFields,
    remove: removeQuestion,
    insert: insertQuestion,
  } = useFieldArray({
    control,
    name: "nestedQuestions",
  });

  const { data: dropdownData, isLoading: isLoadingDropdown } = useList({
    resource: "acceptable-values",
  });

  const { open } = useNotification();

  const { mutate, isLoading } = useCreate();
  const { mutate: updateFormMutate, isLoading: isUpdatingForm } = useUpdate();

  const { list } = useNavigation();

  const [fieldsList, setFieldsList] = useState<FormValuesType[]>([]);
  const [sectionList, setSectionList] = useState<SectionType[]>([]);

  const [querySectionList, setQuerySectionList] = useState<SectionType[]>([]);

  const [editInfo, setEditInfo] = useState(defaultEditInfo);

  const [globalRefs, setGlobalRefs] = useState<Record<string, boolean>>();

  const editSection = ({
    type,
    sectionId,
    fieldId,
    fieldIndex,
  }: {
    type: "edit" | "delete";
    sectionId: string;
    fieldIndex?: number;
    fieldId?: string;
  }) => {
    const sectionIndex = sectionList.findIndex(
      (sec) => sec.sectionId === sectionId
    );

    if (type === "edit") {
      setEditInfo({
        id: sectionId,
        type: "section",
      });

      const sectionName = sectionList[sectionIndex].sectionName;
      const uniqueName = sectionList[sectionIndex].uniqueName;
      const required = sectionList[sectionIndex].required;
      const repeat = sectionList[sectionIndex].repeat;
      const ref = sectionList[sectionIndex].ref;

      resetSectionForm({ sectionName, uniqueName, repeat, required, ref });
    } else {
      if (fieldIndex !== undefined && fieldId) {
        setSectionList((old) => {
          const oldRef = old[sectionIndex].fieldsList[fieldIndex].globalRef;
          if (oldRef.length) {
            setGlobalRefs((old) => {
              if (!old) return old;

              delete old[oldRef];
              return { ...old };
            });
          }
          old[sectionIndex].fieldsList.splice(fieldIndex, 1);
          return [...old];
        });
      } else {
        setSectionList((old) => {
          // remove global refs
          setGlobalRefs((oldRef) => {
            if (!oldRef) return oldRef;

            old[sectionIndex].fieldsList.forEach((field) => {
              if (field.globalRef) {
                delete oldRef[field.globalRef];
              }
            });
            return { ...oldRef };
          });

          old.splice(sectionIndex, 1);
          return [...old];
        });
      }
      setEditInfo(defaultEditInfo);
    }
  };

  const onSubmit = (data: FormValuesType) => {
    if (
      data.fieldType === "checkbox" &&
      data.checkboxOptions.length === 0 &&
      !data.acceptableValuesKey
    ) {
      open?.({
        type: "error",
        message: "Dropdown has no options or reference",
      });
      return;
    }

    if (data.fieldType === "array" && data.nestedQuestions.length === 0) {
      open?.({
        type: "error",
        message: "No nested questions found",
      });
      return;
    }

    if (!data.fieldName.trim() || !data.uniqueName.trim()) {
      open?.({
        type: "error",
        message: "Name field cannot be empty",
      });
      return;
    }

    let ref =
      data.validate_required && data.fieldType !== "array"
        ? data.globalRef.trim()
        : "";

    if (editInfo.type === "field") {
      setFieldsList((old) => {
        const index = old.findIndex((d) => d.id === editInfo.id);

        const oldRef = old[index].globalRef;

        if (ref !== oldRef) {
          // add to list if new ref
          if (ref.length) {
            if (globalRefs?.[ref]) {
              open?.({
                type: "error",
                message: `Global reference with name ${ref} already exists`,
              });

              return old;
            } else {
              setGlobalRefs((old) => ({ ...old, [ref]: true }));
            }
          }

          // remove prev ref whether new ref is added or not
          if (oldRef.length) {
            setGlobalRefs((oldGlobalRef) => ({
              ...oldGlobalRef,
              [oldRef]: false,
            }));
          }
        }
        old[index] = {
          ...data,
          uniqueName: data.uniqueName.trim(),
          globalRef: ref,
        };

        return old;
      });

      setEditInfo(defaultEditInfo);
    } else {
      if (ref.length) {
        if (globalRefs?.[ref]) {
          open?.({
            type: "error",
            message: `Global reference with name ${ref} already exists`,
          });
          return;
        } else {
          setGlobalRefs((old) => ({ ...old, [ref]: true }));
        }
      }
      setFieldsList((old) => [
        ...old,
        {
          ...data,
          uniqueName: data.uniqueName.trim(),
          globalRef: ref,
          id: uuid(),
        },
      ]);
    }
    reset(defaultFormValues);
  };

  const onSectionFormSubmit = ({
    uniqueName,
    sectionName,
    repeat,
    required,
    ref,
  }: SectionFormValuesType) => {
    if (ref.trim().length && !globalRefs?.[ref.trim()]) {
      open?.({
        type: "error",
        message: `Global ref ${ref.trim()} is not defined in field`,
      });
      return;
    }

    if (editInfo.type === "section") {
      const item = sectionList.find(
        (item) =>
          item.sectionId !== editInfo.id && item.uniqueName === uniqueName
      );

      if (item) {
        open?.({
          message: `Section name should be unique. Found duplicate: ${uniqueName}`,
          type: "error",
        });
      } else {
        setSectionList((old) => {
          const itemIndex = old.findIndex(
            (item) => item.sectionId === editInfo.id
          );

          const result = old[itemIndex].fieldsList.find(
            (field) => field.globalRef
          );

          if (result && (repeat || !required)) {
            open?.({
              message: `Repeating or Non required section cannot contain field with global reference`,
              type: "error",
            });
          } else {
            old[itemIndex].sectionName = sectionName;
            old[itemIndex].uniqueName = uniqueName.trim();
            old[itemIndex].repeat = repeat;
            old[itemIndex].required = required;

            if (ref.trim().length && required && repeat) {
              old[itemIndex].ref = ref.trim();
            } else {
              old[itemIndex].ref = "";
            }
          }

          return old;
        });
      }
      setEditInfo(defaultEditInfo);
    } else {
      setSectionList((old) => {
        const item = old.find((item) => item.uniqueName === uniqueName);
        let refValue = "";

        if (item) {
          open?.({
            message: `Section name should be unique. Found duplicate: ${uniqueName}`,
            type: "error",
          });
          return old;
        }

        if (ref.trim().length && required && repeat) {
          refValue = ref.trim();
        }

        return [
          ...old,
          {
            sectionName,
            uniqueName: uniqueName.trim(),
            fieldsList: [],
            sectionId: uuid(),
            repeat,
            required,
            ref: refValue,
          },
        ];
      });
    }
    resetSectionForm(defaultSectionFormValues);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    if (type === Droppables.SECTION) {
      setSectionList((old) => {
        const delData = old.splice(source.index, 1);
        old.splice(destination.index, 0, {
          ...delData[0],
        });

        return old;
      });
    }

    if (type === Droppables.FIELD) {
      if (source.droppableId === Droppables.ALL) {
        let hasError = false;

        setSectionList((old) => {
          const sectionIndex = old.findIndex(
            (section) => section.sectionId === destination.droppableId
          );

          const existingItem = old[sectionIndex].fieldsList.find(
            (fi) => fi.uniqueName === fieldsList[source.index].uniqueName
          );

          if (existingItem) {
            open?.({
              message: `Field name should be unique. Found duplicate: ${existingItem.uniqueName}`,
              type: "error",
            });
            hasError = true;
          } else if (
            fieldsList[source.index].globalRef &&
            (old[sectionIndex].repeat || !old[sectionIndex].required)
          ) {
            open?.({
              message: `Repeating or Non required section cannot contain field with global reference`,
              type: "error",
            });
            hasError = true;
          } else {
            old[sectionIndex].fieldsList.splice(destination.index, 0, {
              ...fieldsList[source.index],
            });
          }

          return old;
        });

        !hasError &&
          setFieldsList((old) => {
            old.splice(source.index, 1);
            return old;
          });
      } else {
        setSectionList((old) => {
          const destSectionIndex = old.findIndex(
            (section) => section.sectionId === destination.droppableId
          );

          const startItemIndex = old.findIndex(
            (section) => section.sectionId === source.droppableId
          );

          const existingItem =
            source.droppableId !== destination.droppableId
              ? old[destSectionIndex].fieldsList.find(
                  (fi) =>
                    fi.uniqueName ===
                    old[startItemIndex].fieldsList[source.index].uniqueName
                )
              : undefined;

          if (existingItem) {
            open?.({
              message: `Field name should be unique. Found duplicate: ${existingItem.uniqueName}`,
              type: "error",
            });
          } else if (
            old[startItemIndex].fieldsList[source.index].globalRef &&
            (old[destSectionIndex].repeat || !old[destSectionIndex].required)
          ) {
            open?.({
              message: `Repeating or Non required section cannot contain field with global reference`,
              type: "error",
            });
          } else {
            const delItem = old[startItemIndex].fieldsList.splice(
              source.index,
              1
            );

            old[destSectionIndex].fieldsList.splice(destination.index, 0, {
              ...delItem[0],
            });
          }

          return old;
        });
      }
    }
  };

  const onFormSubmitHandler = () => {
    if (formData?.status === "PUBLISHED") {
      open?.({
        type: "error",
        message: "Form is already published",
      });

      return;
    }

    const { formName, uniqueName, status } = getValues();

    if (
      formData?.id &&
      JSON.stringify(querySectionList) === JSON.stringify(sectionList) &&
      formData.name === uniqueName.trim() &&
      formData.label === formName.trim()
    ) {
      if (formData.status === status) {
        open?.({
          type: "error",
          message: "No Changes found",
        });
      } else {
        updateFormMutate(
          {
            resource: `forms/${formData.id}/status`,
            id: "",
            values: {
              status,
            },
            meta: {
              method: "patch",
            },
            successNotification: () => {
              return {
                description: "Form status changed",
                message: `Form status successfully changed.`,
                type: "success",
              };
            },
            errorNotification: (errors) => {
              return {
                description: errors?.message,
                message: errors?.errors?.length
                  ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                  : "",
                type: "error",
              };
            },
          },
          {
            onSuccess: () => list("forms"),
          }
        );
      }
      return;
    }

    if (!formName.trim() || !uniqueName.trim()) {
      open?.({
        type: "error",
        message: "Form Info is required",
      });
      return;
    }

    if (!sectionList.length) {
      open?.({
        type: "error",
        message: "Form is empty",
      });
      return;
    }

    const hasEmptySections = sectionList.some(
      (si) => si.fieldsList.length === 0
    );
    if (hasEmptySections) {
      open?.({
        type: "error",
        message: "Empty sections found",
      });
      return;
    }

    const sectionsData: SectionDto[] = sectionList.map(
      (
        { uniqueName, sectionName, fieldsList, repeat, required, ref },
        index
      ) => {
        const fieldsData: FieldDto[] = fieldsList.map((field, index) => {
          const {
            uniqueName,
            fieldName,
            fieldPlaceholder,
            fieldType,
            checkboxOptions,
            multiOptionsSelect,
            nestedQuestions,
            validate_required,
            globalRef,
            fileType,
            acceptableValuesKey,
          } = field;

          let type: FieldEntityTypeEnum = "string";
          let dropdownValues: string[] = [];
          let nestedFields: FieldDto[] = [];

          switch (fieldType) {
            case "string":
              type = "string";
              break;

            case "number":
              type = "number";
              break;

            case "files":
              type = "files";
              break;
            case "checkbox":
              type = multiOptionsSelect ? "multi-choice" : "single-choice";
              dropdownValues = checkboxOptions.map((co) => co.option.trim());
              break;

            case "array":
              type = "fields";
              nestedFields = nestedQuestions.map((field, index) => {
                const {
                  uniqueName,
                  fieldName,
                  fieldPlaceholder,
                  fieldType,
                  checkboxOptions,
                  multiOptionsSelect,
                  validate_required,
                  fileType,
                  acceptableValuesKey,
                } = field;

                let type: FieldEntityTypeEnum = "string";
                let dropdownValues: string[] = [];

                let acceptableKey = "";
                if (dropdownData?.data) {
                  Object.keys(dropdownData.data).forEach((key: any) => {
                    if (
                      (dropdownData?.data?.[key] as any) === acceptableValuesKey
                    ) {
                      acceptableKey = key;
                    }
                  });
                }

                switch (fieldType) {
                  case "string":
                    type = "string";
                    break;

                  case "number":
                    type = "number";
                    break;

                  case "files":
                    type = "files";
                    break;
                  case "checkbox":
                    type = multiOptionsSelect
                      ? "multi-choice"
                      : "single-choice";
                    dropdownValues = checkboxOptions.map((co) =>
                      co.option.trim()
                    );
                    break;

                  case "default":
                    break;
                }
                return {
                  name: uniqueName.trim(),
                  label: fieldName.trim(),
                  placeholder: fieldPlaceholder.trim(),
                  index,
                  required: false, // false for now
                  type,
                  acceptableValues: dropdownValues.length
                    ? dropdownValues
                    : undefined,
                  acceptableValuesKey: acceptableKey || undefined,
                  fileType:
                    type === "files"
                      ? (fileType as FieldDtoFileTypeEnum)
                      : undefined,
                };
              });
              break;
            default:
              break;
          }

          let acceptableKey = "";
          if (dropdownData?.data) {
            Object.keys(dropdownData.data).forEach((key: any) => {
              if ((dropdownData?.data?.[key] as any) === acceptableValuesKey) {
                acceptableKey = key;
              }
            });
          }

          return {
            name: uniqueName.trim(),
            label: fieldName.trim(),
            placeholder: fieldPlaceholder.trim(),
            index,
            required: validate_required,
            type,
            acceptableValues: dropdownValues.length
              ? dropdownValues
              : undefined,
            acceptableValuesKey: acceptableKey || undefined,
            fields: nestedFields.length ? nestedFields : undefined,
            globalRef: globalRef?.trim() || undefined,
            fileType:
              type === "files" ? (fileType as FieldDtoFileTypeEnum) : undefined,
          };
        });

        return {
          name: uniqueName.trim(),
          label: sectionName.trim(),
          index,
          required,
          count:
            ref && globalRefs?.[ref]
              ? {
                  $ref: ref.trim(),
                  type: "number",
                }
              : undefined,
          type: repeat
            ? SectionDtoTypeEnum.Multiple
            : SectionDtoTypeEnum.Single,
          fields: fieldsData,
        };
      }
    );

    const data: CreateFormDto = {
      name: uniqueName.trim(),
      label: formName.trim(),
      sections: sectionsData,
    };

    formData?.id
      ? updateFormMutate(
          {
            resource: "forms",
            id: formData.id,
            values: data,
            meta: {
              method: "put",
            },
            successNotification: () => {
              return {
                description: "Form updated",
                message: `Form successfully updated.`,
                type: "success",
              };
            },
            errorNotification: (errors) => {
              return {
                description: errors?.message,
                message: errors?.errors?.length
                  ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                  : "",
                type: "error",
              };
            },
          },
          {
            onSuccess: () => list("forms"),
          }
        )
      : mutate(
          {
            resource: "forms",
            values: data,
            successNotification: () => {
              return {
                description: "Form created",
                message: `Form successfully created.`,
                type: "success",
              };
            },
            errorNotification: (errors) => {
              return {
                description: errors?.message,
                message: errors?.errors?.length
                  ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                  : "",
                type: "error",
              };
            },
          },
          {
            onSuccess: () => list("forms"),
          }
        );
  };

  useEffect(() => {
    if (isSuccess && formData?.id) {
      const { name, label, status, sections } = formData;
      resetFormInfoForm({
        uniqueName: name,
        formName: label,
        status,
      });

      const sectionData: SectionType[] = sections
        .sort((a, b) => a.index - b.index)
        .map((section) => {
          const { id, name, label, required, type, fields, count } = section;

          const sectionFields: FormValuesType[] = fields
            .sort((a, b) => a.index - b.index)
            .map((field) => {
              const {
                id,
                name,
                label,
                required,
                type,
                fields,
                acceptableValues,
                acceptableValuesKey,
                placeholder,
                globalRef,
                fileType,
              } = field;

              let fieldType: FieldType = "string";
              let checkboxOptions: { option: string }[] = [];
              let nestedQuestions: NestedQuestionsType[] = [];
              let multiOptionsSelect = false;

              switch (type) {
                case "string":
                  fieldType = "string";
                  break;

                case "number":
                  fieldType = "number";
                  break;

                case "files":
                  fieldType = "files";
                  break;

                case "single-choice":
                  fieldType = "checkbox";
                  checkboxOptions = acceptableValues
                    ? acceptableValues.map((v) => ({ option: v }))
                    : [];
                  multiOptionsSelect = false;
                  break;

                case "multi-choice":
                  fieldType = "checkbox";
                  checkboxOptions = acceptableValues
                    ? acceptableValues.map((v) => ({ option: v }))
                    : [];
                  multiOptionsSelect = true;
                  break;

                case "fields":
                  fieldType = "array";
                  nestedQuestions = fields
                    ? fields.map((field) => {
                        const {
                          name,
                          label,
                          required,
                          type,
                          acceptableValues,
                          acceptableValuesKey,
                          placeholder,
                          fileType,
                        } = field;

                        let fieldType: FieldType = "string";
                        let checkboxOptions: { option: string }[] = [];
                        let multiOptionsSelect = false;

                        switch (type) {
                          case "string":
                            fieldType = "string";
                            break;

                          case "number":
                            fieldType = "number";
                            break;

                          case "files":
                            fieldType = "files";
                            break;

                          case "single-choice":
                            fieldType = "checkbox";
                            checkboxOptions = acceptableValues
                              ? acceptableValues.map((v) => ({ option: v }))
                              : [];
                            multiOptionsSelect = false;
                            break;

                          case "multi-choice":
                            fieldType = "checkbox";
                            checkboxOptions = acceptableValues
                              ? acceptableValues.map((v) => ({ option: v }))
                              : [];
                            multiOptionsSelect = true;
                            break;

                          default:
                            break;
                        }
                        return {
                          uniqueName: name,
                          fieldName: label,
                          fieldType,
                          fieldPlaceholder: placeholder || "",
                          checkboxOptions,
                          acceptableValuesKey: acceptableValuesKey || null,
                          multiOptionsSelect,
                          validate_required: required,
                          fileType: fileType || "",
                        };
                      })
                    : [];
                  break;
                default:
                  break;
              }

              if (globalRef) {
                setGlobalRefs((old) => ({ ...old, [globalRef]: true }));
              }

              return {
                ...defaultFormValues,
                id,
                uniqueName: name,
                fieldName: label,
                fieldType,
                fieldPlaceholder: placeholder || "",
                checkboxOptions,
                acceptableValuesKey: acceptableValuesKey || null,
                nestedQuestions,
                multiOptionsSelect,
                validate_required: required,
                globalRef: globalRef || "",
                fileType: fileType || "",
              };
            });

          return {
            sectionId: id,
            uniqueName: name,
            sectionName: label,
            required,
            repeat: type === "multiple",
            fieldsList: sectionFields,
            ref: count?.$ref || "",
          };
        });

      setQuerySectionList(JSON.parse(JSON.stringify(sectionData)));
      setSectionList(JSON.parse(JSON.stringify(sectionData)));
    }
  }, [isSuccess, formData, resetFormInfoForm]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid container alignItems={"flex-start"}>
        {/* First section */}
        <Grid
          container
          item
          p={theme.commonPadding}
          alignItems={"flex-start"}
          gap={theme.gap}
          overflow={"scroll"}
          height={"80vh"}
          md={6}
        >
          {/* Create section */}
          <Box
            flex={1}
            display={"flex"}
            justifyContent={"space-between"}
            flexDirection={"row"}
            alignItems={"center"}
            mr={2}
          >
            <Typography variant="h5">Add a form section</Typography>
          </Box>

          <Grid container item>
            <Box
              component={"form"}
              display={"flex"}
              flexDirection={"column"}
              flex={1}
              p={theme.commonPadding}
              gap={theme.gap}
              onSubmit={handleSubmitSectionForm(onSectionFormSubmit)}
            >
              <FormControl variant="standard">
                <Controller
                  name="sectionName"
                  control={sectionFormControl}
                  render={({ field }) => (
                    <TextField
                      label={"Section Label: "}
                      required
                      {...field}
                      placeholder="Add section name"
                    />
                  )}
                />
              </FormControl>

              <FormControl variant="standard">
                <Controller
                  name="uniqueName"
                  control={sectionFormControl}
                  render={({ field }) => (
                    <TextField
                      label={"Unique section name: "}
                      required
                      {...field}
                      placeholder="Add a unique section name"
                    />
                  )}
                />
              </FormControl>

              {watchSectionForm("required") && watchSectionForm("repeat") ? (
                <FormControl variant="standard">
                  <Controller
                    name="ref"
                    control={sectionFormControl}
                    render={({ field }) => (
                      <TextField
                        label={"Link field"}
                        {...field}
                        placeholder="Add a field reference name"
                      />
                    )}
                  />
                </FormControl>
              ) : null}

              <FormControlLabel
                label={"Required"}
                control={
                  <Controller
                    name={"required"}
                    control={sectionFormControl}
                    render={({ field: { value, ...field } }) => (
                      <Checkbox {...field} checked={!!value} />
                    )}
                  />
                }
              />

              <FormControlLabel
                label={"Repeat section"}
                control={
                  <Controller
                    name={"repeat"}
                    control={sectionFormControl}
                    render={({ field: { value, ...field } }) => (
                      <Checkbox {...field} checked={!!value} />
                    )}
                  />
                }
              />

              <ButtonGroup>
                <Button fullWidth type={"submit"} variant="contained">
                  {editInfo.type === "section" ? "Edit Section" : "Add Section"}
                </Button>

                {editInfo.type === "section" ? (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      resetSectionForm(defaultSectionFormValues);
                      setEditInfo(defaultEditInfo);
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
              </ButtonGroup>
            </Box>
          </Grid>

          {/* Create field */}
          <Typography variant="h5">Add a form field</Typography>

          <Grid container item>
            <Box
              component={"form"}
              display={"flex"}
              flexDirection={"column"}
              flex={1}
              p={theme.commonPadding}
              gap={theme.gap}
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormControl variant="standard">
                <Controller
                  name="fieldName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={"Field Label:"}
                      required
                      {...field}
                      placeholder="Enter field name"
                    />
                  )}
                />
              </FormControl>

              <FormControl variant="standard">
                <Controller
                  name="uniqueName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={"Unique field name: "}
                      required
                      {...field}
                      placeholder="Add a unique field name"
                    />
                  )}
                />
              </FormControl>

              <FormControl variant="standard">
                <Controller
                  name="fieldPlaceholder"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={"Placeholder:"}
                      {...field}
                      placeholder="Enter placeholder text"
                    />
                  )}
                />
              </FormControl>

              {watch("validate_required") && watch("fieldType") !== "array" ? (
                <FormControl variant="standard">
                  <Controller
                    name="globalRef"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label={"Unique reference name:"}
                        {...field}
                        placeholder="Enter unique name for reference"
                      />
                    )}
                  />
                </FormControl>
              ) : null}

              <FormControl variant="standard">
                <Controller
                  name="fieldType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      label={"Type:"}
                      select
                      placeholder="Select field type"
                    >
                      {fieldTypeOptions.map(({ label, value }, i) => (
                        <MenuItem key={label + i} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </FormControl>

              {watch("fieldType") === "files" ? (
                <FormControl variant="standard">
                  <Controller
                    name="fileType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        label={"File Type:"}
                        select
                        placeholder="Select file type"
                      >
                        {fileTypeOptions.map(({ label, value }, i) => (
                          <MenuItem key={label + i} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </FormControl>
              ) : null}

              {watch("fieldType") === "checkbox" ? (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"flex-start"}
                  gap={theme.gap}
                  border={theme.borderPrimary}
                  borderRadius={theme.borderRadius}
                  p={theme.commonPadding}
                >
                  <FormControlLabel
                    label={"Allow Multi select"}
                    control={
                      <Controller
                        name="multiOptionsSelect"
                        control={control}
                        render={({ field: { value, ...field } }) => (
                          <Checkbox {...field} checked={!!value} />
                        )}
                      />
                    }
                  />

                  <FormControl variant="standard" fullWidth>
                    <Controller
                      name="acceptableValuesKey"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          loading={isLoadingDropdown}
                          options={
                            dropdownData?.data
                              ? (Object.values(
                                  dropdownData.data
                                ) as unknown as string[])
                              : []
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={"Select dropdrown items"}
                              placeholder={"Select dropdrown items"}
                            />
                          )}
                          onChange={(_, value) => field.onChange(value)}
                        />
                      )}
                    />
                  </FormControl>

                  <Divider
                    sx={{
                      width: 1,
                    }}
                  />

                  <FormControl variant="standard" fullWidth>
                    <Controller
                      name="checkboxOption"
                      control={control}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label={"Options:"}
                            placeholder="Enter an option: "
                          />

                          <Stack
                            direction={"row"}
                            mt={2}
                            flexWrap={"wrap"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                          >
                            {fields.map((checkboxOptions, index) => {
                              return (
                                <Chip
                                  key={index}
                                  label={checkboxOptions.option}
                                  onDelete={() => {
                                    remove(index);
                                  }}
                                  sx={{
                                    margin: 1,
                                    height: "auto",
                                    "& .MuiChip-label": {
                                      display: "block",
                                      whiteSpace: "normal",
                                    },
                                  }}
                                />
                              );
                            })}
                          </Stack>

                          <Button
                            sx={{ mt: 2 }}
                            variant={"contained"}
                            onClick={() => {
                              const value = watch("checkboxOption");

                              if (!value.length) {
                                open?.({
                                  type: "error",
                                  message: "No Dropdown options found",
                                });
                                return;
                              }

                              insert(watch("checkboxOptions").length, {
                                option: value,
                              });

                              setValue("checkboxOption", "");
                            }}
                          >
                            Add option
                          </Button>
                        </>
                      )}
                    />
                  </FormControl>
                </Box>
              ) : null}

              {watch("fieldType") === "array" ? (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"flex-start"}
                  flex={1}
                  gap={theme.gap}
                  border={theme.borderPrimary}
                  borderRadius={theme.borderRadius}
                  p={theme.commonPadding}
                >
                  <FormControl variant="standard" fullWidth>
                    <Controller
                      name="nestedQuestion"
                      control={control}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label={"Question:"}
                            placeholder="Enter a question: "
                          />
                        </>
                      )}
                    />
                  </FormControl>

                  <FormControl variant="standard" fullWidth>
                    <Controller
                      name="nestedQuestionUniqueName"
                      control={control}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label={"Unique question name:"}
                            placeholder="Enter a unique question name: "
                          />
                        </>
                      )}
                    />
                  </FormControl>

                  <FormControl variant="standard" fullWidth>
                    <Controller
                      name="nestedFieldPlaceholder"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label={"Placeholder:"}
                          {...field}
                          placeholder="Enter placeholder text"
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl variant="standard" fullWidth>
                    <Controller
                      name="nestedQuestionType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={"Type:"}
                          select
                          placeholder="Enter question type"
                        >
                          {questionTypeOptions.map(({ label, value }, i) => (
                            <MenuItem key={label + i} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </FormControl>

                  {watch("nestedQuestionType") === "files" ? (
                    <FormControl variant="standard" fullWidth>
                      <Controller
                        name="nestedFileType"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            label={"File Type:"}
                            select
                            placeholder="Select file type"
                          >
                            {fileTypeOptions.map(({ label, value }, i) => (
                              <MenuItem key={label + i} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </FormControl>
                  ) : null}

                  {watch("nestedQuestionType") === "checkbox" ? (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"flex-start"}
                      width={"100%"}
                      flex={1}
                      gap={theme.gap}
                      border={theme.borderPrimary}
                      borderRadius={theme.borderRadius}
                      p={theme.commonPadding}
                    >
                      <FormControlLabel
                        label={"Allow Multi select"}
                        control={
                          <Controller
                            name="nestedMultiOptionsSelect"
                            control={control}
                            render={({ field: { value, ...field } }) => (
                              <Checkbox {...field} checked={!!value} />
                            )}
                          />
                        }
                      />

                      <FormControl variant="standard" fullWidth>
                        <Controller
                          name="nestedAcceptableValuesKey"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              loading={isLoadingDropdown}
                              options={
                                dropdownData?.data
                                  ? (Object.values(
                                      dropdownData.data
                                    ) as unknown as string[])
                                  : []
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={"Select dropdrown items"}
                                  placeholder={"Select dropdrown items"}
                                />
                              )}
                              onChange={(_, value) => field.onChange(value)}
                            />
                          )}
                        />
                      </FormControl>

                      <Divider
                        sx={{
                          width: 1,
                        }}
                      />

                      <FormControl variant="standard" fullWidth>
                        <Controller
                          name="nestedCheckboxOption"
                          control={control}
                          render={({ field }) => (
                            <>
                              <TextField
                                {...field}
                                label={"Options:"}
                                placeholder="Enter an option: "
                              />

                              <Stack
                                direction={"row"}
                                mt={2}
                                flexWrap={"wrap"}
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                              >
                                {nestedFields.map((checkboxOptions, index) => {
                                  return (
                                    <Chip
                                      key={index}
                                      label={checkboxOptions.option}
                                      onDelete={() => {
                                        nestedRemove(index);
                                      }}
                                      sx={{
                                        margin: 1,
                                        height: "auto",
                                        "& .MuiChip-label": {
                                          display: "block",
                                          whiteSpace: "normal",
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Stack>

                              <Button
                                sx={{ mt: 2 }}
                                variant={"contained"}
                                onClick={() => {
                                  const value = watch("nestedCheckboxOption");

                                  if (!value.length) {
                                    open?.({
                                      type: "error",
                                      message: "No Dropdown options found",
                                    });
                                    return;
                                  }

                                  nestedInsert(
                                    watch("nestedCheckboxOptions").length,
                                    {
                                      option: value,
                                    }
                                  );

                                  setValue("nestedCheckboxOption", "");
                                }}
                              >
                                Add option
                              </Button>
                            </>
                          )}
                        />
                      </FormControl>
                    </Box>
                  ) : null}

                  {/* <FormControlLabel
                    label={"Required"}
                    control={
                      <Controller
                        name="nested_validate_required"
                        control={control}
                        render={({ field: { value, ...field } }) => (
                          <Checkbox {...field} checked={!!value} />
                        )}
                      />
                    }
                  /> */}

                  <Stack
                    direction={"row"}
                    flexWrap={"wrap"}
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                  >
                    {questionFields.map((question, index) => {
                      return (
                        <Chip
                          key={index}
                          label={question.fieldName}
                          onDelete={() => {
                            removeQuestion(index);
                          }}
                          sx={{
                            margin: 1,
                            height: "auto",
                            "& .MuiChip-label": {
                              display: "block",
                              whiteSpace: "normal",
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>

                  <Button
                    variant={"contained"}
                    fullWidth
                    onClick={() => {
                      const fieldName = watch("nestedQuestion");
                      const uniqueName = watch("nestedQuestionUniqueName");
                      const fieldType = watch("nestedQuestionType");
                      const fileType = watch("nestedFileType");
                      const fieldPlaceholder = watch("nestedFieldPlaceholder");
                      const acceptableValuesKey = watch(
                        "nestedAcceptableValuesKey"
                      );
                      const checkboxOptions = watch("nestedCheckboxOptions");
                      const multiOptionsSelect = watch(
                        "nestedMultiOptionsSelect"
                      );
                      const validate_required = watch(
                        "nested_validate_required"
                      );

                      if (!fieldName.trim() || !uniqueName.trim()) {
                        open?.({
                          type: "error",
                          message: "Name field cannot be empty",
                        });
                        return;
                      }

                      if (!fieldType) {
                        open?.({
                          type: "error",
                          message: "Field type cannot be empty",
                        });
                        return;
                      }

                      if (
                        fieldType === "checkbox" &&
                        checkboxOptions.length === 0 &&
                        !acceptableValuesKey
                      ) {
                        open?.({
                          type: "error",
                          message: "Dropdown has no options or reference",
                        });
                        return;
                      }

                      const item = questionFields.find(
                        (question) => question.uniqueName === uniqueName.trim()
                      );

                      if (item) {
                        open?.({
                          message: `Question name in nested field should be unique. Found duplicate: ${uniqueName}`,
                          type: "error",
                        });
                        return;
                      }

                      insertQuestion(watch("nestedQuestions").length, {
                        fieldName,
                        uniqueName,
                        fieldType,
                        fileType,
                        fieldPlaceholder,
                        acceptableValuesKey,
                        checkboxOptions,
                        multiOptionsSelect,
                        validate_required,
                      });

                      setValue("nestedQuestion", "");
                      setValue("nestedQuestionUniqueName", "");
                      setValue("nestedQuestionType", "");
                      setValue("nestedFileType", "");
                      setValue("nestedFieldPlaceholder", "");
                      setValue("nestedAcceptableValuesKey", null);
                      setValue("nestedCheckboxOptions", []);
                      setValue("nestedMultiOptionsSelect", false);
                      // setValue("nested_validate_required", false);
                    }}
                  >
                    Add question
                  </Button>
                </Box>
              ) : null}

              <FormControlLabel
                label={"Required"}
                control={
                  <Controller
                    name="validate_required"
                    control={control}
                    render={({ field: { value, ...field } }) => (
                      <Checkbox {...field} checked={!!value} />
                    )}
                  />
                }
              />

              <ButtonGroup fullWidth>
                <Button
                  variant="contained"
                  style={{ letterSpacing: 3 }}
                  type="submit"
                >
                  {editInfo.type === "field" ? "EDIT" : "CREATE"}
                </Button>

                {editInfo.type === "field" ? (
                  <Button
                    variant="contained"
                    style={{ letterSpacing: 3 }}
                    onClick={() => {
                      reset(defaultFormValues);
                      setEditInfo(defaultEditInfo);
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
              </ButtonGroup>
            </Box>
          </Grid>

          {fieldsList.length ? (
            <Typography variant={"h5"}>Fields list</Typography>
          ) : null}

          <Droppable
            droppableId={Droppables.ALL}
            type={Droppables.FIELD}
            isDropDisabled
          >
            {(provided) => (
              <Grid
                container
                flexDirection={"column"}
                gap={theme.gap}
                p={theme.commonPadding}
                border={fieldsList.length ? theme.borderPrimary : ""}
                borderRadius={theme.borderRadius}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {fieldsList.map((item, i) => {
                  return (
                    <FieldComponent
                      key={item.id}
                      index={i}
                      item={item}
                      edit
                      editHandler={() => {
                        setEditInfo({
                          id: item.id,
                          type: "field",
                        });
                        reset({ ...item });
                      }}
                      resetFormWithValues={() => {
                        editInfo.type === "field" &&
                          setEditInfo(defaultEditInfo);
                        reset({ ...item });
                      }}
                      deleteHandler={() => {
                        setFieldsList((old) => {
                          if (old[i].globalRef.length) {
                            setGlobalRefs((oldRef) => {
                              if (!oldRef) return oldRef;

                              delete oldRef[old[i].globalRef];

                              return { ...oldRef };
                            });
                          }
                          old.splice(i, 1);

                          return [...old];
                        });

                        setEditInfo(defaultEditInfo);
                      }}
                      isEditing={editInfo.id === item.id}
                    />
                  );
                })}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </Grid>

        {/* Second section */}
        <Grid
          container
          item
          p={theme.commonPadding}
          overflow={"scroll"}
          height={"80vh"}
          alignItems={"flex-start"}
          md={6}
        >
          <Grid
            item
            xs={12}
            p={theme.commonPadding}
            alignItems={"flex-start"}
            border={theme.borderPrimary}
            borderRadius={theme.borderRadius}
          >
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              flex={1}
            >
              <Typography variant={"h5"}>Form preview</Typography>

              <Button
                disabled={isLoading || isUpdatingForm}
                onClick={onFormSubmitHandler}
                variant={"contained"}
              >
                Save
              </Button>
            </Box>

            <Grid container mt={2}>
              <Accordion
                style={{
                  flex: 1,
                }}
                defaultExpanded
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls={`formInfo`}
                  id={`formInfo`}
                  sx={{
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    style={{
                      maxWidth: "400px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    Form Info
                  </Typography>
                </AccordionSummary>

                <AccordionDetails style={{ wordBreak: "break-all" }}>
                  <Box
                    component={"form"}
                    display={"flex"}
                    flexDirection={"column"}
                    flex={1}
                    gap={theme.gap}
                  >
                    <FormControl variant="standard">
                      <Controller
                        name="formName"
                        control={formInfoFormControl}
                        render={({ field }) => (
                          <TextField
                            label={"Form Label:"}
                            required
                            {...field}
                            placeholder="Enter form name"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl variant="standard">
                      <Controller
                        name="uniqueName"
                        control={formInfoFormControl}
                        render={({ field }) => (
                          <TextField
                            label={"Unique form name: "}
                            required
                            {...field}
                            placeholder="Add a unique form name"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl variant="standard">
                      <Controller
                        name="status"
                        control={formInfoFormControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            label={"Status:"}
                            select
                            placeholder="Select form status"
                            disabled={
                              !formData?.id || formData.status === "PUBLISHED"
                            }
                          >
                            {formStatusTypeOptions.map(
                              ({ label, value }, i) => (
                                <MenuItem key={label + i} value={value}>
                                  {label}
                                </MenuItem>
                              )
                            )}
                          </TextField>
                        )}
                      />
                    </FormControl>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {sectionList.length ? (
              <Droppable
                droppableId={Droppables.SECTION}
                type={Droppables.SECTION}
              >
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      flexDirection: "column",
                      gap: theme.gap,
                      backgroundColor: snapshot.isDraggingOver
                        ? colors.blue["50"]
                        : "white",
                    }}
                  >
                    {sectionList.map(
                      (
                        {
                          sectionName,
                          uniqueName,
                          fieldsList,
                          sectionId,
                          repeat,
                          required,
                          ref,
                        },
                        i
                      ) => {
                        return (
                          <SectionItem
                            key={sectionId}
                            index={i}
                            uniqueName={uniqueName}
                            fieldsList={fieldsList}
                            sectionName={sectionName}
                            sectionId={sectionId}
                            editSection={editSection}
                            repeat={repeat}
                            required={required}
                            fieldRef={ref}
                            isEditing={editInfo.id === sectionId}
                            resetFormWithValues={(item: FormValuesType) => {
                              editInfo.type === "field" &&
                                setEditInfo(defaultEditInfo);
                              reset({ ...item });
                            }}
                          />
                        );
                      }
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </DragDropContext>
  );
};
